/**
 * Frontend Telemetry - Error and Event Logging
 *
 * Sends errors and events to our API, which logs to CloudWatch.
 * Query errors with CloudWatch Logs Insights.
 *
 * Example query:
 *   fields @timestamp, errorMessage, userId, url
 *   | filter level = "error"
 *   | sort @timestamp desc
 *   | limit 50
 */

const API_BASE = '/v1';
const IS_DEV = import.meta.env.DEV;
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || 'development';

/**
 * Telemetry sampling + per-session caps.
 *
 * Without these, an error-spamming bug in any user's browser becomes a
 * self-DDoS on /v1/telemetry/errors and a meaningful CloudWatch ingest
 * cost at beta scale. The caps and the sample rate are deliberately
 * conservative defaults — a real telemetry storm should burn fast and
 * stop, not silently throttle into invisibility.
 *
 * VITE_TELEMETRY_SAMPLE: float in [0,1]. 1.0 = send every event.
 *   Default 1.0 today (we want full signal during beta); turn down later.
 * MAX_ERRORS_PER_SESSION_PER_LEVEL: hard cap per browser-tab session.
 *   `info` and `warn` are noisier than `error`, so split by level.
 * `error`-level events are NEVER sampled out (sample rate = 1.0 for them)
 *   — they're already cheaper and we want every real failure.
 */
const TELEMETRY_SAMPLE_RATE = (() => {
  const raw = import.meta.env.VITE_TELEMETRY_SAMPLE;
  if (typeof raw !== 'string') return 1.0;
  const parsed = parseFloat(raw);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) return 1.0;
  return parsed;
})();
const MAX_ERRORS_PER_SESSION_PER_LEVEL: Record<'error' | 'warn' | 'info', number> = {
  error: 50,
  warn: 50,
  info: 100,
};
const sessionErrorCounts: Record<'error' | 'warn' | 'info', number> = {
  error: 0,
  warn: 0,
  info: 0,
};
let telemetryCapWarned = false;

interface TelemetryUser {
  userId: string;
  email: string;
  roles?: string[];
}

interface ErrorPayload {
  level: 'error' | 'warn' | 'info';
  message: string;
  stack?: string;
  url: string;
  userAgent: string;
  timestamp: string;
  environment: string;
  userId?: string;
  userEmail?: string;
  context?: Record<string, unknown>;
  breadcrumbs?: Breadcrumb[];
}

interface Breadcrumb {
  timestamp: string;
  category: string;
  message: string;
  data?: Record<string, unknown>;
}

interface TraceEntry {
  timestamp: string;
  name: string;
  phase: 'start' | 'end' | 'error';
  duration?: number;
  data?: Record<string, unknown>;
}

interface ApiTraceEntry {
  timestamp: string;
  method: string;
  url: string;
  phase: 'request' | 'response' | 'error';
  status?: number;
  duration?: number;
  requestBody?: unknown;
  responseBody?: unknown;
  error?: string;
  requestSize?: number;
  responseSize?: number;
  responseText?: string; // Raw response when JSON parsing fails
}

// In-memory state
let currentUser: TelemetryUser | null = null;
const breadcrumbs: Breadcrumb[] = [];
const MAX_BREADCRUMBS = 20;
const traces: TraceEntry[] = [];
const MAX_TRACES = 50;
const apiTraces: ApiTraceEntry[] = [];
const MAX_API_TRACES = 30;

// Queue for batching (send errors even if one fails)
let errorQueue: ErrorPayload[] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;

// Dev log queue - sends logs to local dev endpoint for aggregation
interface DevLogEntry {
  timestamp: string;
  level: string;
  message: string;
  [key: string]: unknown;
}
let devLogQueue: DevLogEntry[] = [];
let devFlushTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Queue a log entry for the dev log file (only in dev mode)
 */
function queueDevLog(level: string, message: string, data?: Record<string, unknown>) {
  if (!IS_DEV) return;

  devLogQueue.push({
    timestamp: new Date().toISOString(),
    level: level.toUpperCase(),
    message,
    url: window.location.pathname,
    userId: currentUser?.userId,
    ...data,
  });

  // Debounce flush
  if (devFlushTimeout) {
    clearTimeout(devFlushTimeout);
  }

  if (devLogQueue.length >= 10) {
    flushDevLogs();
  } else {
    devFlushTimeout = setTimeout(flushDevLogs, 500);
  }
}

/**
 * Flush dev logs to the local dev endpoint
 */
async function flushDevLogs() {
  if (devLogQueue.length === 0) return;

  const logs = devLogQueue;
  devLogQueue = [];
  devFlushTimeout = null;

  try {
    await fetch(`${API_BASE}/dev/logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ logs }),
    });
  } catch {
    // Silently fail - dev logging shouldn't break the app
  }
}

// Active traces for timing
const activeTraces: Map<string, { startTime: number; name: string }> = new Map();

/**
 * Initialize telemetry - sets up global error handlers
 */
export function initTelemetry() {
  // Capture unhandled errors
  window.addEventListener('error', (event) => {
    captureError(event.error || new Error(event.message), {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    captureError(error, { type: 'unhandledrejection' });
  });

  // Capture console.error calls
  const originalConsoleError = console.error;
  console.error = (...args) => {
    originalConsoleError.apply(console, args);
    const message = args
      .map((arg) => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg)))
      .join(' ');
    addBreadcrumb('console', message);
  };

  // Track navigation
  window.addEventListener('popstate', () => {
    addBreadcrumb('navigation', `Navigate to ${window.location.pathname}`);
  });

  // Track clicks on interactive elements
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'BUTTON' || target.tagName === 'A' || target.closest('button, a')) {
        const element = target.closest('button, a') || target;
        const text = element.textContent?.slice(0, 50) || '';
        const id = element.id || element.getAttribute('data-testid') || '';
        addBreadcrumb('ui.click', `Click: ${text}${id ? ` (${id})` : ''}`);
      }
    },
    { capture: true },
  );

  if (!IS_DEV) {
    console.log(`[Telemetry] Initialized for ${ENVIRONMENT}`);
  }
}

/**
 * Set user context for error attribution
 */
export function setTelemetryUser(user: TelemetryUser) {
  currentUser = user;
  addBreadcrumb('auth', `User signed in: ${user.email}`);
}

/**
 * Clear user context on sign out
 */
export function clearTelemetryUser() {
  if (currentUser) {
    addBreadcrumb('auth', 'User signed out');
  }
  currentUser = null;
}

/**
 * Add a breadcrumb for debugging context
 */
export function addBreadcrumb(category: string, message: string, data?: Record<string, unknown>) {
  breadcrumbs.push({
    timestamp: new Date().toISOString(),
    category,
    message: message.slice(0, 200), // Limit length
    data,
  });

  // Keep only recent breadcrumbs
  if (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs.shift();
  }
}

/**
 * Start a trace for a function/flow - call traceEnd when done
 * Returns a trace ID to pass to traceEnd
 */
export function traceStart(name: string, data?: Record<string, unknown>): string {
  const traceId = `${name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  activeTraces.set(traceId, { startTime: performance.now(), name });

  traces.push({
    timestamp: new Date().toISOString(),
    name,
    phase: 'start',
    data,
  });

  if (traces.length > MAX_TRACES) {
    traces.shift();
  }

  // Also log to console and dev file in dev
  if (IS_DEV) {
    console.log(`[Trace] START: ${name}`, data || '');
    queueDevLog('DEBUG', `[Trace] START: ${name}`, { traceId, ...data });
  }

  return traceId;
}

/**
 * End a trace started with traceStart
 */
export function traceEnd(traceId: string, data?: Record<string, unknown>) {
  const active = activeTraces.get(traceId);
  if (!active) {
    console.warn(`[Trace] No active trace found for ID: ${traceId}`);
    return;
  }

  const duration = performance.now() - active.startTime;
  activeTraces.delete(traceId);

  traces.push({
    timestamp: new Date().toISOString(),
    name: active.name,
    phase: 'end',
    duration,
    data,
  });

  if (traces.length > MAX_TRACES) {
    traces.shift();
  }

  if (IS_DEV) {
    console.log(`[Trace] END: ${active.name} (${duration.toFixed(2)}ms)`, data || '');
    queueDevLog('DEBUG', `[Trace] END: ${active.name}`, { traceId, duration, ...data });
  }
}

/**
 * Record a trace error
 */
export function traceError(traceId: string, error: Error | string, data?: Record<string, unknown>) {
  const active = activeTraces.get(traceId);
  const name = active?.name || 'unknown';
  const duration = active ? performance.now() - active.startTime : undefined;

  if (active) {
    activeTraces.delete(traceId);
  }

  traces.push({
    timestamp: new Date().toISOString(),
    name,
    phase: 'error',
    duration,
    data: {
      ...data,
      error: error instanceof Error ? error.message : error,
    },
  });

  if (traces.length > MAX_TRACES) {
    traces.shift();
  }

  if (IS_DEV) {
    console.log(`[Trace] ERROR: ${name}`, error, data || '');
    queueDevLog('ERROR', `[Trace] ERROR: ${name}`, {
      traceId,
      duration,
      error: error instanceof Error ? error.message : error,
      ...data,
    });
  }
}

/**
 * Track an API call - call at request start
 */
export function traceApiRequest(method: string, url: string, body?: unknown): string {
  const traceId = `api-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const requestSize = body ? JSON.stringify(body).length : 0;

  activeTraces.set(traceId, { startTime: performance.now(), name: `${method} ${url}` });

  apiTraces.push({
    timestamp: new Date().toISOString(),
    method,
    url,
    phase: 'request',
    requestBody: sanitizeForLogging(body),
    requestSize,
  });

  if (apiTraces.length > MAX_API_TRACES) {
    apiTraces.shift();
  }

  if (IS_DEV) {
    console.log(`[API] REQUEST: ${method} ${url}`, body ? `(${requestSize} bytes)` : '');
    queueDevLog('INFO', `[API] REQUEST: ${method} ${url}`, { traceId, requestSize });
  }

  return traceId;
}

/**
 * Track API response
 */
export function traceApiResponse(traceId: string, status: number, body?: unknown) {
  const active = activeTraces.get(traceId);
  const duration = active ? performance.now() - active.startTime : undefined;

  if (active) {
    activeTraces.delete(traceId);
  }

  apiTraces.push({
    timestamp: new Date().toISOString(),
    method: active?.name.split(' ')[0] || 'UNKNOWN',
    url: active?.name.split(' ').slice(1).join(' ') || 'unknown',
    phase: 'response',
    status,
    duration,
    responseBody: sanitizeForLogging(body),
  });

  if (apiTraces.length > MAX_API_TRACES) {
    apiTraces.shift();
  }

  if (IS_DEV) {
    console.log(`[API] RESPONSE: ${status} (${duration?.toFixed(2)}ms)`);
    queueDevLog('INFO', `[API] RESPONSE: ${status}`, { traceId, status, duration });
  }
}

/**
 * Extended API error details for better debugging
 */
interface ApiErrorDetails {
  status?: number;
  responseText?: string;
  responseSize?: number;
  requestSize?: number;
}

/**
 * Track API error with detailed context
 */
export function traceApiError(traceId: string, error: Error | string, details?: ApiErrorDetails) {
  const active = activeTraces.get(traceId);
  const duration = active ? performance.now() - active.startTime : undefined;
  const [method, ...urlParts] = (active?.name || 'UNKNOWN unknown').split(' ');
  const url = urlParts.join(' ') || 'unknown';

  if (active) {
    activeTraces.delete(traceId);
  }

  const errorEntry: ApiTraceEntry = {
    timestamp: new Date().toISOString(),
    method,
    url,
    phase: 'error',
    duration,
    error: error instanceof Error ? error.message : error,
    status: details?.status,
    responseText: details?.responseText?.slice(0, 500), // Limit size
    responseSize: details?.responseSize,
    requestSize: details?.requestSize,
  };

  apiTraces.push(errorEntry);

  if (apiTraces.length > MAX_API_TRACES) {
    apiTraces.shift();
  }

  if (IS_DEV) {
    console.log(`[API] ERROR:`, error, details ? `(status: ${details.status})` : '');
    queueDevLog('ERROR', `[API] ERROR: ${method} ${url}`, {
      traceId,
      error: error instanceof Error ? error.message : error,
      status: details?.status,
      duration,
    });
  }

  // Immediately send critical API errors to CloudWatch
  if (!IS_DEV && details?.status) {
    sendApiErrorReport(errorEntry);
  }
}

/**
 * Send an API error report immediately to CloudWatch
 */
async function sendApiErrorReport(errorEntry: ApiTraceEntry) {
  const state = getDebugState();

  try {
    await fetch(`${API_BASE}/telemetry/errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        errors: [
          {
            level: 'error',
            message: `API Error: ${errorEntry.method} ${errorEntry.url} - ${errorEntry.error}`,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: errorEntry.timestamp,
            environment: ENVIRONMENT,
            userId: currentUser?.userId,
            userEmail: currentUser?.email,
            context: {
              apiError: {
                method: errorEntry.method,
                url: errorEntry.url,
                status: errorEntry.status,
                duration: errorEntry.duration,
                responseText: errorEntry.responseText,
                responseSize: errorEntry.responseSize,
                requestSize: errorEntry.requestSize,
                error: errorEntry.error,
              },
              recentApiTraces: apiTraces.slice(-5),
            },
            breadcrumbs: state.breadcrumbs,
          },
        ],
      }),
    });
  } catch {
    // Silently fail - don't cause more errors
  }
}

/**
 * Sanitize objects for logging (remove large/sensitive data)
 */
function sanitizeForLogging(obj: unknown): unknown {
  if (!obj) return obj;
  if (typeof obj !== 'object') return obj;

  const str = JSON.stringify(obj);
  if (str.length > 1000) {
    return { _truncated: true, _length: str.length, _preview: str.slice(0, 200) };
  }
  return obj;
}

/**
 * Get current debug state - useful for manual debugging
 */
export function getDebugState() {
  return {
    user: currentUser ? { userId: currentUser.userId, email: currentUser.email } : null,
    breadcrumbs: [...breadcrumbs],
    traces: [...traces],
    apiTraces: [...apiTraces],
    activeTraces: Array.from(activeTraces.entries()).map(([id, t]) => ({
      id,
      name: t.name,
      elapsedMs: performance.now() - t.startTime,
    })),
    environment: ENVIRONMENT,
    url: window.location.href,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Print a debug snapshot to the console for troubleshooting.
 *
 * Intentionally does not POST to a server endpoint — without auth + rate
 * limiting that becomes an XSS-amplifier and a state-exfiltration vector.
 * Errors that need server-side capture should flow through `captureError()`,
 * which writes to the authenticated logs pipeline.
 */
export function sendDebugReport(reason: string): void {
  const state = getDebugState();
  console.log('[Telemetry] Debug Report:', reason, state);
}

// Expose debug functions globally for console debugging
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).fiasDebug = {
    getState: getDebugState,
    sendReport: sendDebugReport,
  };
}

/**
 * Capture and report an error
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
  const payload: ErrorPayload = {
    level: 'error',
    message: error.message,
    stack: error.stack,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    environment: ENVIRONMENT,
    userId: currentUser?.userId,
    userEmail: currentUser?.email,
    context: sanitizeContext(context),
    breadcrumbs: [...breadcrumbs],
  };

  queueError(payload);
}

/**
 * Capture a warning or info message
 */
export function captureMessage(
  message: string,
  level: 'warn' | 'info' = 'info',
  context?: Record<string, unknown>,
) {
  const payload: ErrorPayload = {
    level,
    message,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    environment: ENVIRONMENT,
    userId: currentUser?.userId,
    userEmail: currentUser?.email,
    context: sanitizeContext(context),
    breadcrumbs: [...breadcrumbs],
  };

  queueError(payload);
}

/**
 * Remove sensitive data from context
 */
function sanitizeContext(context?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!context) return undefined;

  const sanitized = { ...context };
  const sensitiveKeys = ['password', 'token', 'secret', 'authorization', 'cookie', 'credential'];

  for (const key of Object.keys(sanitized)) {
    if (sensitiveKeys.some((sk) => key.toLowerCase().includes(sk))) {
      sanitized[key] = '[REDACTED]';
    }
  }

  return sanitized;
}

/**
 * Queue error for batched sending. Applies the per-session cap and the
 * sampling rate before enqueueing — both are pre-network guards: a rejected
 * event never touches the queue, never hits the network, never adds load.
 *
 * `error`-level events bypass sampling (they're cheap and important). The
 * per-session cap still applies to error-level events — otherwise an
 * effect-loop error becomes its own DoS.
 */
function queueError(payload: ErrorPayload) {
  const level = payload.level;

  // Per-session cap. Once exceeded, drop silently for this session — emit
  // a single console warning the first time so a developer eyeballing the
  // tab can see why telemetry went quiet.
  if (sessionErrorCounts[level] >= MAX_ERRORS_PER_SESSION_PER_LEVEL[level]) {
    if (!telemetryCapWarned) {
      telemetryCapWarned = true;
      console.warn(
        `[telemetry] session cap reached for level=${level}; subsequent events of this level will be dropped`,
      );
    }
    return;
  }

  // Sample non-error levels. Errors are never sampled out.
  if (level !== 'error' && TELEMETRY_SAMPLE_RATE < 1.0) {
    if (Math.random() >= TELEMETRY_SAMPLE_RATE) {
      return;
    }
  }

  sessionErrorCounts[level]++;
  errorQueue.push(payload);

  // Debounce: send after 1 second of no new errors, or immediately if queue is large
  if (flushTimeout) {
    clearTimeout(flushTimeout);
  }

  if (errorQueue.length >= 5) {
    flushErrors();
  } else {
    flushTimeout = setTimeout(flushErrors, 1000);
  }
}

/**
 * Test/debug hook: reset the per-session cap state. Not for production
 * code — there's no path that legitimately resets the cap mid-session.
 */
export function _resetTelemetrySessionCapsForTesting(): void {
  sessionErrorCounts.error = 0;
  sessionErrorCounts.warn = 0;
  sessionErrorCounts.info = 0;
  telemetryCapWarned = false;
}

/**
 * Send queued errors to the API
 */
async function flushErrors() {
  if (errorQueue.length === 0) return;

  const errors = errorQueue;
  errorQueue = [];
  flushTimeout = null;

  // In dev mode, log to console and send to dev endpoint
  if (IS_DEV) {
    errors.forEach((err) => {
      console.log('[Telemetry]', err.level.toUpperCase(), err.message);
      queueDevLog(err.level.toUpperCase(), err.message, {
        stack: err.stack,
        context: err.context,
      });
    });
    return;
  }

  try {
    await fetch(`${API_BASE}/telemetry/errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ errors }),
    });
  } catch {
    // Silently fail - don't cause more errors trying to report errors
    // Re-queue errors for retry? For now, just drop them.
  }
}

/**
 * Debug logging - logs to both console and dev log file
 * Use for temporary debugging that should be visible in logs/dev.jsonl
 */
export function logDebug(category: string, message: string, data?: Record<string, unknown>) {
  const fullMessage = `[${category}] ${message}`;
  console.log(fullMessage, data || '');
  queueDevLog('DEBUG', fullMessage, data);
}

/**
 * Simple ErrorBoundary component for React
 */
import { Component, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack: string }) {
    captureError(error, {
      componentStack: errorInfo.componentStack,
      type: 'react-error-boundary',
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}
